import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const FALLBACK_BG = {
  background:
    "radial-gradient(120% 90% at 20% 15%, #e2a85c 0%, transparent 60%), radial-gradient(80% 70% at 85% 85%, #2a4365 0%, transparent 70%), linear-gradient(160deg, #101522, #080a0f)",
};

const fmt = (n) =>
  `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

function ArtworkCard({ art, onRemoved }) {
  const [buying, setBuying] = useState(false);
  const sold = art.status === "sold";

  const buy = async () => {
    setBuying(true);
    try {
      const res = await api.post("/payments/checkout", {
        artwork_id: art.id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout could not be started");
      setBuying(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${art.title}" from the gallery?`)) return;
    try {
      await api.delete(`/artworks/${art.id}`);
      toast.success(`"${art.title}" removed`);
      onRemoved?.(art.id);
    } catch {
      toast.error("Could not remove artwork");
    }
  };

  return (
    <article
      className="bg-ground-card border border-line p-4 flex flex-col group"
      data-testid={`artwork-card-${art.id}`}
    >
      <div className="relative w-full aspect-[4/5] overflow-hidden bg-ground-elevated mb-4">
        {art.image_url ? (
          <img
            src={art.image_url}
            alt={art.title}
            className={`w-full h-full object-cover art-drift ${sold ? "opacity-50 grayscale-[35%]" : ""}`}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full art-drift" style={FALLBACK_BG} />
        )}
        <span className="absolute top-3 left-3 bg-black/70 backdrop-blur text-[10px] mono-tag uppercase px-2 py-1 text-slate-200">
          {art.badge || art.medium}
        </span>
        {sold && (
          <span
            className="absolute inset-0 flex items-center justify-center"
            data-testid={`artwork-sold-badge-${art.id}`}
          >
            <span className="mono-tag text-xs tracking-[0.3em] uppercase bg-black/70 backdrop-blur px-4 py-2 text-gold border border-gold">
              Acquired
            </span>
          </span>
        )}
      </div>
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-lg font-bold text-white uppercase">{art.title}</h3>
        <span className="text-gold font-bold text-base mono-tag">{fmt(art.price)}</span>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        {art.dimensions} &middot; {art.medium}
      </p>
      <div className="mt-auto flex gap-2">
        <button
          type="button"
          onClick={buy}
          disabled={sold || buying}
          className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
            sold
              ? "bg-line text-slate-500 cursor-not-allowed"
              : "bg-gold text-black hover:bg-gold-hover"
          } disabled:opacity-70`}
          data-testid={`purchase-button-${art.id}`}
        >
          {sold ? "Sold — In Collection" : buying ? "Preparing Checkout…" : "Purchase Artwork"}
        </button>
        <button
          type="button"
          onClick={remove}
          title="Remove piece"
          className="px-3 border border-line text-slate-500 hover:border-red-400 hover:text-red-400 transition-colors"
          data-testid={`remove-button-${art.id}`}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
          </svg>
        </button>
      </div>
    </article>
  );
}

export default function Gallery({ artworks, loading, onRemoved }) {
  return (
    <section id="gallery" className="py-24 border-b border-line">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
          <div>
            <span className="mono-tag text-xs uppercase text-gold tracking-widest block mb-2">
              Curated Catalog
            </span>
            <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight text-white">
              Finished Originals
            </h2>
          </div>
          <div className="mt-4 md:mt-0 flex items-center gap-4">
            <p className="text-sm text-slate-400 max-w-xs">
              Signed, varnished, and prepared for immediate secure worldwide
              shipping.
            </p>
            <a
              href="#add-piece"
              className="px-4 py-2 border border-slate-600 text-xs font-bold uppercase text-white hover:border-gold hover:text-gold transition-colors whitespace-nowrap"
              data-testid="gallery-add-piece-btn"
            >
              + Add Piece
            </a>
          </div>
        </div>

        {loading ? (
          <p className="text-slate-400 mono-tag text-xs uppercase tracking-widest" data-testid="gallery-loading">
            Loading catalog…
          </p>
        ) : artworks.length === 0 ? (
          <div
            className="p-8 border border-dashed border-line-light bg-ground-alt text-center"
            data-testid="gallery-empty-state"
          >
            <p className="text-sm text-slate-400 max-w-md mx-auto">
              No pieces listed yet. Use the studio form above to publish your
              first original.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="gallery-grid">
            {artworks.map((art) => (
              <ArtworkCard key={art.id} art={art} onRemoved={onRemoved} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
